//
//
//
//
//
//
//
//
//
//
//
//
//

import PQueue from 'p-queue';
import { mapGetters } from 'vuex';
import { PALETTE_COLOURS } from '@/store/types/getters';
import { COLOURS } from '@/store/types/namespaces';
import { importPlotly } from './Plotly';
import BaseChart from './baseChart';

const queue = new PQueue({ concurrency: 1 });
async function throttleDraw(Plotly, element, traces, layout) {
  await queue.add(() => new Promise((resolve) => {
    setTimeout(() => {
      if (!element) return resolve();
      return Plotly.react(element, traces, layout, { showLink: false }).then(() => {
        resolve();
      });
    }, 50);
  }));
}

const States = {
  INIT: 0,
  SETUP: 1,
  ERROR: 2,
  READY: 3,
};

export default {
  name: 'DpSimpleChart',
  mixins: [BaseChart],
  props: {
    data: { type: Array, default: null },
    options: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      readyToDisplay: false,
      currentState: States.INIT,
      States,
    };
  },
  computed: {
    ...mapGetters(COLOURS, [PALETTE_COLOURS]),
    isStacked() {
      return this.options && this.options.stack && this.options.stack.field;
    },
  },
  watch: {
    data: {
      immediate: true,
      deep: true,
      handler() {
        this.update();
      },
    },
    options: {
      deep: true,
      handler() {
        this.update();
      },
    },
  },
  methods: {
    hasValidOptions() {
      if (!this.options) return false;

      const { __typename } = this.options;
      if (__typename === 'pie' && (!this.options.values.field || !this.options.labels.field)) {
        return false;
      }
      if (__typename === 'pie' && this.options.values.field && this.options.labels.field) {
        return true;
      }

      // Check xaxis
      if (!this.options.xaxis || !this.options.xaxis.field) {
        return false;
      }
      // Check yaxis
      if (!this.options.yaxis || !this.options.yaxis.field) {
        return false;
      }
      return true;
    },
    getDisplayOptions() {
      const { options: { __typename: visualisation } } = this;
      if (!visualisation) {
        return { type: 'bar' };
      }
      if (visualisation === 'horizontalBar') {
        return { type: 'bar', orientation: 'h' };
      }
      if (visualisation === 'line') {
        return { type: 'line', mode: 'lines+markers' };
      }
      if (visualisation === 'scatter') {
        return { type: 'scatter', mode: 'markers' };
      }
      if (visualisation === 'filledArea') {
        return { type: 'line', mode: 'lines', fill: 'tonexty' };
      }
      return { type: visualisation };
    },
    getLayout() {
      const c = this[PALETTE_COLOURS];

      const { xaxis, yaxis, chart } = this.options;
      let xaxisType;
      let yaxisType;
      if (xaxis) {
        xaxisType = xaxis.type ? xaxis.type : 'category';
      }
      if (yaxis) {
        yaxisType = yaxis.type ? yaxis.type : '-';
      }

      const chartFont = 'Proxima Nova, Arial';
      const titlefont = {
        family: chartFont,
        size: 16,
        color: c.neutral[5],
      };
      const tickfont = {
        family: chartFont,
        size: 12,
        color: c.neutral[5],
      };

      const layout = {
        xaxis: {
          type: xaxisType,
          range: [],
          automargin: true,
          gridcolor: c.neutral[0],
          titlefont,
          tickfont,
          title: {
            text: chart.xaxisTitle,
          },
        },
        yaxis: {
          type: yaxisType,
          fixedrange: true,
          range: [null, null],
          gridcolor: c.neutral[0],
          automargin: true,
          titlefont,
          tickfont,
          title: {
            text: chart.yaxisTitle,
          },
        },
        autosize: true,
        margin: this.margins,
        font: {
          family: chartFont,
        },
        plot_bgcolor: 'transparent',
        paper_bgcolor: 'transparent',
        legend: {
          orientation: 'v',
          traceorder: 'normal',
          font: {
            color: c.dark,
          },
        },
      };

      const { stack } = this.options;

      if (chart.title) {
        layout.title = {
          text: chart.title,
          font: {
            ...titlefont,
            size: 18,
          },
        };
      }

      if (stack) {
        layout.barmode = stack.type ? stack.type : 'stack';
      }

      return layout;
    },
    async getSingleTraces() {
      const {
        type,
        mode,
        fill,
        orientation,
      } = this.getDisplayOptions();

      const { chart, xaxis, yaxis } = this.options;

      const trace = {
        type,
        mode,
        fill,
        orientation,
        x: [],
        y: [],
        hoverinfo: 'x+y+text',
        marker: {
          color: [],
        },
        line: {
          color: [],
          width: 2,
          shape: chart && chart.lineStyle ? chart.lineStyle : 'linear',
        },
      };

      const c = this[PALETTE_COLOURS];

      this.data.forEach((d) => {
        trace.x.push(d[xaxis.field]);
        trace.y.push(d[yaxis.field]);
        trace.marker.color.push(c.chart[0]);
      });

      return [trace];
    },
    async getPieTraces() {
      const trace = {
        type: 'pie',
        values: [],
        labels: [],
      };

      const { values, labels } = this.options;

      this.data.forEach((d) => {
        trace.values.push(d[values.field]);
        trace.labels.push(d[labels.field]);
      });

      return [trace];
    },
    async getStackedTraces() {
      const {
        type,
        mode,
        fill,
        orientation,
      } = this.getDisplayOptions();

      const traces = [];
      const { xaxis, yaxis, stack } = this.options;
      const stackField = stack.field;

      const stacked = {};
      this.data.forEach((d) => {
        const stackValue = d[stackField];
        if (!stacked[stackValue]) {
          stacked[stackValue] = [d];
        } else {
          stacked[stackValue].push(d);
        }
      });

      let colourIndex = 0;
      Object.keys(stacked).forEach((name) => {
        const trace = {
          name,
          type,
          mode,
          fill,
          orientation,
          x: [],
          y: [],
          hoverinfo: 'name+x+y+text',
          marker: {
            color: [],
          },
          line: {
            color: [],
            width: 2,
            shape: this.options && this.options.lineStyle ? this.options.lineStyle : 'linear',
          },
        };

        const { chart } = this[PALETTE_COLOURS];

        stacked[name].forEach((d) => {
          trace.x.push(d[xaxis.field]);
          trace.y.push(d[yaxis.field]);
          trace.marker.color.push(chart[colourIndex]);
        });

        colourIndex += 1;
        traces.push(trace);
      });

      return traces;
    },
    async update() {
      const { Plotly } = await importPlotly();
      Plotly.purge(this.$refs.element);

      if (!this.hasValidOptions()) {
        this.currentState = States.SETUP;
        return;
      }

      let traces;
      if (this.options.__typename === 'pie') {
        traces = await this.getPieTraces();
      } else if (this.isStacked) {
        traces = await this.getStackedTraces();
      } else {
        traces = await this.getSingleTraces();
      }

      const layout = this.getLayout();

      throttleDraw(Plotly, this.$refs.element, traces, layout).then(() => {
        if (!this.$refs.element) return;

        this.canResize = true;
        this.resize();
      });
    },
  },
};
